import React from "react";
import type { EditorialDiagnostics, EditorialFailureReason } from "@shared/editorialExecution";

const REASON_LABELS: Partial<Record<EditorialFailureReason, string>> = {
  provider_cooldown: "A retained provider retry hint paused this request",
  configured_pause: "The configured server model budget is paused",
  missing_key: "No Groq key is set",
  no_eligible_model: "No eligible free model was available",
  capability_unavailable: "No eligible model supports the required response format",
  transport_denied: "The provider request was blocked by transport policy",
  authentication: "The provider rejected the key",
  permission: "The key does not have permission for this request",
  entitlement: "The provider reported a payment or entitlement restriction",
  rate_limit: "The provider rate limit was reached",
  permanent_request: "The provider rejected the request",
  temporary_provider: "The provider is temporarily unavailable",
  temporary_network: "A temporary network error occurred",
  attempt_timeout: "The provider request timed out",
  invalid_output: "The model response did not pass validation",
  response_too_large: "The provider response exceeded the size limit",
  cancelled: "The request was cancelled",
  deadline: "The request reached its time limit",
  capacity: "Editorial generation is at capacity",
  evidence_expired: "The assessment inputs or validity window changed during processing",
  insufficient_evidence: "There was not enough eligible evidence",
  cache_invalid: "The saved result no longer matches current evidence",
};

function reasonLabel(reason: EditorialFailureReason | null): string | null {
  return reason ? REASON_LABELS[reason] ?? "The provider request could not be completed" : null;
}

function cacheLabel(diagnostics: EditorialDiagnostics): string {
  const { source, read, write } = diagnostics.cache;
  if (read === "hit") {
    if (source === "redis") return "Redis cache hit";
    if (source === "memory") return "Memory cache hit";
    return "Cache hit; source unspecified";
  }
  if (read === "unavailable" || write === "unavailable") return "Cache unavailable; result generated for this request";
  if (read === "error" || write === "error") return "Cache error; result generated for this request";
  if (read === "invalid") return "Cached result rejected; result generated for this request";
  if (read === "miss") return "Cache miss; result generated for this request";
  return "Cache status unavailable";
}

export default function EditorialDiagnosticsDisplay({ diagnostics }: { diagnostics?: EditorialDiagnostics }) {
  if (!diagnostics) return null;

  const provider = diagnostics.resolvedProvider
    ? `${diagnostics.resolvedProvider} · ${diagnostics.resolvedModel ?? "model ID not supplied"}`
    : diagnostics.requestedProvider && diagnostics.requestedModel
      ? `${diagnostics.requestedProvider} requested · no model result`
      : "No model used";
  const outcome = diagnostics.outcome === "model"
    ? "Model-assisted editorial selection"
    : diagnostics.outcome === "heuristic"
      ? "Heuristic editorial selection"
      : "Insufficient eligible evidence";
  const fallback = reasonLabel(diagnostics.fallbackReason);
  const failedAttempts = diagnostics.attempts.filter((attempt) => attempt.reason);

  return (
    <section aria-label="Editorial processing details" style={{ border: "1px solid #30291f", padding: "10px 12px", marginBottom: 16, color: "#aaa", fontSize: 11 }}>
      <p style={{ margin: "0 0 6px", color: "#ddd" }}>{outcome} · confidence is editorial and unvalidated, not a probability.</p>
      {diagnostics.partial && <p style={{ margin: "0 0 4px" }}>Partial provider output: the provider reported a token limit. Accepted selections passed validation; output coverage may be incomplete.</p>}
      <p style={{ margin: "0 0 4px" }}>Provider/model: {provider}</p>
      <p style={{ margin: "0 0 4px" }}>Policy: free-only · required capabilities: text generation and JSON response</p>
      <p style={{ margin: "0 0 4px" }}>Cache: {cacheLabel(diagnostics)}{diagnostics.cache.coalesced ? " · shared in-progress request" : ""}</p>
      {diagnostics.cooldown && <p style={{ margin: "0 0 4px" }}>
        {diagnostics.cooldown.source === "operator_guard" ? "Configured server pause" : "Retained provider retry hint"}: {diagnostics.cooldown.until ? `until ${diagnostics.cooldown.until}` : "invalid pause configuration; requests are blocked"}.
        {diagnostics.attempts.length === 0 ? " No provider request was sent for this assessment." : " The original provider attempt is retained below."}
      </p>}
      {fallback && <p style={{ margin: "0 0 4px" }}>Reason: {fallback}</p>}
      {failedAttempts.length > 0 && (
        <details>
          <summary style={{ cursor: "pointer" }}>Provider attempts ({failedAttempts.length})</summary>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {failedAttempts.map((attempt, index) => (
              <li key={`${attempt.requestedModel}-${index}`}>
                {attempt.requestedModel}: {reasonLabel(attempt.reason)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
